import { useEffect, useState } from "react";
import { Plus, Trash2, Save, QrCode, Printer, Copy, MapPin, BookOpen } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api, mediaUrl } from "@/lib/api";
import { usePublicSiteBase } from "@/lib/usePublicSiteBase";
import { LAKE_DAY_PAGES } from "@/data/lakeDayGuide";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import MediaUpload from "@/components/admin/MediaUpload";

const blank = { title: "", description: "", image_url: "", video_url: "", price_note: "", link_url: "", access_code: "", code_message: "", code_image_url: "", card_image_url: "", info_content: "", guide_pages: [], map_spots: [], returnable_items: [], keepable_items: [], order: 0 };

const PICNIC_PRESET = [
  { title: "Lost Dutchman State Park", description: "Picnic ramadas at the foot of the Superstition Mountains with epic views.", latitude: 33.4599, longitude: -111.4776 },
  { title: "Prospector Park", description: "Apache Junction park with ramadas, playground and mountain views.", latitude: 33.4396, longitude: -111.5424 },
  { title: "Flatiron Community Park", description: "Central Apache Junction park with shaded tables, splash pad and lawns.", latitude: 33.4173, longitude: -111.5485 },
  { title: "Usery Mountain Regional Park", description: "Desert picnic ramadas near the Wind Cave Trail and archery range.", latitude: 33.4726, longitude: -111.6003 },
  { title: "Granite Reef Recreation Site", description: "Shaded Salt River day-use area on Bush Highway — tables and river access.", latitude: 33.5138, longitude: -111.6829 },
  { title: "Butcher Jones Beach (Saguaro Lake)", description: "Sandy swim beach with shaded picnic areas and an easy lakeside trail.", latitude: 33.5698, longitude: -111.5088 },
  { title: "Acacia Recreation Site", description: "Salt River day-use spot on Bush Highway with picnic tables and shade.", latitude: 33.5369, longitude: -111.4317 },
  { title: "Painted Sky Park", description: "Newer Apache Junction park (Blossom Rock) with ramadas and open play areas.", latitude: 33.3258, longitude: -111.5540 },
  { title: "Canyon Lake", description: "Turquoise water framed by red cliffs — ramadas, boat ramp and shady tables.", latitude: 33.5347, longitude: -111.4228 },
  { title: "Saguaro Lake", description: "Desert reservoir with marina, picnic areas and scenic canyon views.", latitude: 33.5709, longitude: -111.5090 },
];

function MapSpotsEditor({ spots, onChange }) {
  const list = spots || [];
  const update = (i, patch) => onChange(list.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const add = () => onChange([...list, { title: "", description: "", latitude: "", longitude: "" }]);
  const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));
  const locate = async (i) => {
    const s = list[i];
    if (!(s.title || "").trim()) { toast.error("Enter a place name first"); return; }
    try {
      const { data } = await api.get("/geocode", { params: { q: s.title } });
      if (data && data.latitude != null) { update(i, { latitude: data.latitude, longitude: data.longitude }); toast.success("Location found"); }
      else toast.error("Couldn't find that place — enter coordinates manually");
    } catch { toast.error("Location lookup failed"); }
  };
  return (
    <div data-testid="addon-map-editor" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="flex items-center gap-2"><MapPin size={15} className="text-brand-terracotta" /> Picnic Spots (map pins) <span className="text-brand-forest/40">({list.length})</span></Label>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange(PICNIC_PRESET)} data-testid="map-load-preset" className="rounded-full">Load 10 Apache Junction spots</Button>
      </div>
      <div className="space-y-3">
        {list.map((s, i) => (
          <div key={i} className="rounded-lg bg-white/70 border border-black/5 p-3 space-y-2" data-testid={`map-spot-${i}`}>
            <div className="flex gap-2 items-center">
              <span className="text-xs font-semibold text-brand-forest/40 w-5">{i + 1}</span>
              <Input data-testid={`map-spot-title-${i}`} value={s.title || ""} onChange={(e) => update(i, { title: e.target.value })} placeholder="Place name (e.g. Canyon Lake)" />
              <button type="button" data-testid={`map-spot-remove-${i}`} onClick={() => removeAt(i)} className="shrink-0 text-brand-forest/30 hover:text-destructive px-1"><Trash2 size={16} /></button>
            </div>
            <Input data-testid={`map-spot-desc-${i}`} value={s.description || ""} onChange={(e) => update(i, { description: e.target.value })} placeholder="Short description (optional)" />
            <div className="flex gap-2 items-center">
              <Input data-testid={`map-spot-lat-${i}`} value={s.latitude ?? ""} onChange={(e) => update(i, { latitude: e.target.value })} placeholder="Latitude" inputMode="decimal" className="font-mono text-sm" />
              <Input data-testid={`map-spot-lng-${i}`} value={s.longitude ?? ""} onChange={(e) => update(i, { longitude: e.target.value })} placeholder="Longitude" inputMode="decimal" className="font-mono text-sm" />
              <Button type="button" variant="outline" size="sm" onClick={() => locate(i)} data-testid={`map-spot-locate-${i}`} className="rounded-full shrink-0"><MapPin size={14} className="mr-1" />Find</Button>
            </div>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="map-spot-add" className="rounded-full"><Plus size={14} className="mr-1.5" />Add spot</Button>
    </div>
  );
}

function GuidePagesEditor({ pages, onChange, editingId }) {
  const list = pages || [];
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const update = (i, patch) => onChange(list.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const add = () => onChange([...list, { title: "", subtitle: "", content: "" }]);
  const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div data-testid="addon-guide-editor" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="flex items-center gap-2"><BookOpen size={15} className="text-brand-terracotta" /> Guide Pages <span className="text-brand-forest/40">(each becomes a selection in the QR hub · {list.length})</span></Label>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange(LAKE_DAY_PAGES)} data-testid="guide-load-lakeday" className="rounded-full">Load Lake Day guide (Canyon + Saguaro)</Button>
      </div>
      <div className="space-y-4">
        {list.map((p, i) => (
          <div key={i} className="rounded-lg bg-white/70 border border-black/5 p-3 space-y-2" data-testid={`guide-page-${i}`}>
            <div className="flex gap-2 items-center">
              <span className="text-xs font-semibold text-brand-forest/40 w-5">{i + 1}</span>
              <Input data-testid={`guide-title-${i}`} value={p.title || ""} onChange={(e) => update(i, { title: e.target.value })} placeholder="Selection title (e.g. Canyon Lake)" />
              <button type="button" data-testid={`guide-remove-${i}`} onClick={() => removeAt(i)} className="shrink-0 text-brand-forest/30 hover:text-destructive px-1"><Trash2 size={16} /></button>
            </div>
            <Input data-testid={`guide-subtitle-${i}`} value={p.subtitle || ""} onChange={(e) => update(i, { subtitle: e.target.value })} placeholder="Short subtitle (optional)" />
            <Textarea data-testid={`guide-content-${i}`} rows={6} value={p.content || ""} onChange={(e) => update(i, { content: e.target.value })} placeholder={"Guide text.\nUse ALL-CAPS lines as headers, \"- \" for bullets, \"---\" for a divider, and [label](https://link) for links."} />
            {editingId && (p.title || p.content) && (
              <a href={`${origin}/guide/${editingId}/${i}`} target="_blank" rel="noreferrer" data-testid={`guide-preview-${i}`} className="text-xs text-brand-terracotta hover:underline inline-flex items-center gap-1">Preview this page →</a>
            )}
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="guide-add" className="rounded-full"><Plus size={14} className="mr-1.5" />Add guide page</Button>
      <p className="text-xs text-brand-forest/50">Tip: use ALL-CAPS lines for section headers, lines starting with "- " for bullets, "- [ ]" for checklists, "---" for a divider, and [label](https://link) for tappable links.</p>
    </div>
  );
}

function ItemListEditor({ label, hint, items, onChange, testid }) {
  const list = items || [];
  const update = (i, val) => onChange(list.map((it, idx) => (idx === i ? val : it)));
  const add = () => onChange([...list, ""]);
  const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <Label className="mb-1.5 block">{label} {hint && <span className="text-brand-forest/40">{hint}</span>}</Label>
      <div className="space-y-2">
        {list.map((it, i) => (
          <div key={i} className="flex gap-2">
            <Input data-testid={`${testid}-input-${i}`} value={it} onChange={(e) => update(i, e.target.value)} placeholder="e.g. Cooler, chairs, umbrella" />
            <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => removeAt(i)} className="shrink-0 text-brand-forest/30 hover:text-destructive transition-colors px-2"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testid}-add`} className="mt-2 rounded-full"><Plus size={14} className="mr-1.5" />Add item</Button>
    </div>
  );
}

export default function EditAddons() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingPay, setSavingPay] = useState(false);
  const [savingMsg, setSavingMsg] = useState(false);

  const load = () => api.get("/addons").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const openNew = () => setEditing({ ...blank, order: items.length });
  const save = async () => {
    if (!editing.title) { toast.error("Title required"); return; }
    const payload = {
      ...editing,
      guide_pages: (editing.guide_pages || [])
        .map((p) => ({ title: (p.title || "").trim(), subtitle: (p.subtitle || "").trim(), content: (p.content || "").trim() }))
        .filter((p) => p.title || p.content),
      map_spots: (editing.map_spots || [])
        .map((s) => ({ title: s.title || "", description: s.description || "", latitude: parseFloat(s.latitude), longitude: parseFloat(s.longitude) }))
        .filter((s) => s.title && !isNaN(s.latitude) && !isNaN(s.longitude)),
    };
    if (editing.id) await api.put(`/addons/${editing.id}`, payload);
    else await api.post("/addons", payload);
    toast.success("Saved"); setEditing(null); load();
  };
  const remove = async (id) => { await api.delete(`/addons/${id}`); toast.success("Deleted"); load(); };

  const origin = usePublicSiteBase();
  const packUrl = editing?.id ? `${origin}/pack/${editing.id}` : "";
  const mapUrl = editing?.id ? `${origin}/picnic-map/${editing.id}` : "";
  const hubUrl = editing?.id ? `${origin}/basket/${editing.id}` : "";
  const destinationCount = editing
    ? (editing.guide_pages || []).filter((p) => (p.title || p.content)).length + (editing.info_content?.trim() ? 1 : 0) + (editing.map_spots?.length ? 1 : 0)
    : 0;
  const printQRFrom = (testid, caption) => {
    const canvas = document.querySelector(`[data-testid="${testid}"] canvas`);
    if (!canvas) return;
    const img = canvas.toDataURL("image/png");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${editing?.title || "Add-on"} QR</title></head>
      <body style="font-family:Arial;text-align:center;padding:40px">
      <h2 style="color:#2C3B2E">${editing?.title || "Add-on"}</h2>
      <p style="color:#666">${caption}</p>
      <img src="${img}" style="width:300px;height:300px"/>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const savePayments = async () => {
    setSavingPay(true);
    try {
      await api.put("/settings", settings);
      toast.success("Payment details saved");
    } catch { toast.error("Could not save payment details"); }
    finally { setSavingPay(false); }
  };

  const saveMessage = async () => {
    setSavingMsg(true);
    try {
      await api.put("/settings", settings);
      toast.success("Access code message saved");
    } catch { toast.error("Could not save message"); }
    finally { setSavingMsg(false); }
  };

  const payFields = [
    { key: "zelle", label: "Zelle", ph: "email or phone linked to Zelle" },
    { key: "cashapp", label: "Cash App", ph: "$YourCashtag" },
    { key: "venmo", label: "Venmo", ph: "@your-venmo" },
    { key: "paypal", label: "PayPal", ph: "paypal.me/you or email" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><span className="overline text-brand-terracotta">Edit</span><h1 className="font-display text-4xl tracking-tight mt-1">Add-Ons & Services</h1></div>
        <Button onClick={openNew} data-testid="add-addon" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors"><Plus size={16} className="mr-2" />Add</Button>
      </div>

      {settings && (
        <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm mb-8" data-testid="payment-details">
          <h2 className="font-display text-2xl tracking-tight">Payment Details</h2>
          <p className="text-sm text-brand-forest/60 mt-1 mb-4">These auto-appear to guests when they pick a payment method. The account name is shown so guests can verify they're paying the right person.</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
            {payFields.map((f) => (
              <div key={f.key} className="rounded-xl border border-black/5 p-4">
                <p className="font-display text-lg tracking-tight mb-3">{f.label}</p>
                <div className="space-y-3">
                  <div>
                    <Label className="mb-1.5 block text-xs uppercase tracking-wide text-brand-forest/50">Payment info</Label>
                    <Input data-testid={`admin-pay_${f.key}`} value={settings[`pay_${f.key}`] || ""} placeholder={f.ph}
                      onChange={(e) => setSettings({ ...settings, [`pay_${f.key}`]: e.target.value })} />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-xs uppercase tracking-wide text-brand-forest/50">Account name</Label>
                    <Input data-testid={`admin-pay_${f.key}_name`} value={settings[`pay_${f.key}_name`] || ""} placeholder="Name on the account"
                      onChange={(e) => setSettings({ ...settings, [`pay_${f.key}_name`]: e.target.value })} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={savePayments} disabled={savingPay} data-testid="save-payments" className="mt-5 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white">
            <Save size={16} className="mr-2" />{savingPay ? "Saving…" : "Save Payment Details"}
          </Button>
        </div>
      )}

      {settings && (
        <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm mb-8" data-testid="code-message-panel">
          <h2 className="font-display text-2xl tracking-tight">Access Code Message</h2>
          <p className="text-sm text-brand-forest/80 mt-1 mb-4">This is the exact message texted or emailed to the guest when you tap the <strong>"Send Code"</strong> button. Use <span className="font-mono bg-brand-sand px-1 rounded">{"{code}"}</span> where the access code should appear and <span className="font-mono bg-brand-sand px-1 rounded">{"{title}"}</span> for the add-on name.</p>
          <Label htmlFor="admin-code-message" className="mb-1.5 block">Message sent to the guest</Label>
          <Textarea id="admin-code-message" data-testid="admin-code-message" rows={3}
            value={settings.code_message_template ?? ""}
            placeholder="Your access code for {title}: {code}. Use it to open the lock. Enjoy! — MyAZbnb.com"
            onChange={(e) => setSettings({ ...settings, code_message_template: e.target.value })} />
          <Button onClick={saveMessage} disabled={savingMsg} data-testid="save-code-message" className="mt-4 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white">
            <Save size={16} className="mr-2" />{savingMsg ? "Saving…" : "Save Message"}
          </Button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`admin-addon-${it.id}`} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm flex justify-between gap-3">
            <div className="flex gap-3">
              {it.image_url && <img src={mediaUrl(it.image_url)} alt="" className="h-14 w-14 rounded-xl object-cover" />}
              <div>
                <h3 className="font-display text-lg tracking-tight">{it.title} {it.price_note && <span className="text-brand-terracotta text-sm">· {it.price_note}</span>}</h3>
                <p className="text-sm text-brand-forest/60 line-clamp-2 mt-1">{it.description}</p>
                {!it.link_url && (
                  <p className="text-xs mt-1.5 text-brand-forest/50">Access code: <span className="font-mono font-semibold text-brand-forest">{it.access_code || "— not set —"}</span></p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={() => setEditing(it)} data-testid={`edit-addon-${it.id}`} className="text-sm font-medium text-brand-terracotta hover:underline">Edit</button>
              <button onClick={() => remove(it.id)} data-testid={`delete-addon-${it.id}`} className="text-brand-forest/30 hover:text-destructive transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="addon-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Edit" : "New"} Add-On</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label className="mb-1.5 block">Title</Label><Input data-testid="addon-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Price note</Label><Input data-testid="addon-price" value={editing.price_note} onChange={(e) => setEditing({ ...editing, price_note: e.target.value })} placeholder="$40" /></div>
              </div>
              <div><Label className="mb-1.5 block">Description</Label><Textarea data-testid="addon-desc" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div><Label className="mb-1.5 block">Link URL <span className="text-brand-forest/40">(optional — if set, the button opens this link directly instead of the payment form, e.g. an affiliate sign-up page)</span></Label>
                <Input data-testid="addon-link" value={editing.link_url || ""} onChange={(e) => setEditing({ ...editing, link_url: e.target.value })} placeholder="https://www.myazbnb.com/affiliate" />
              </div>
              {!editing.link_url && (
                <div><Label className="mb-1.5 block">Access Code <span className="text-brand-forest/40">(the lock code sent to the buyer after you confirm payment)</span></Label>
                  <Input data-testid="addon-access-code" value={editing.access_code || ""} onChange={(e) => setEditing({ ...editing, access_code: e.target.value })} placeholder="e.g. 4821" />
                </div>
              )}
              {!editing.link_url && (
                <div><Label htmlFor="addon-code-message" className="mb-1.5 block">Custom Access Code Message <span className="text-brand-forest/40">(optional — overrides the default message for THIS add-on. Use <span className="font-mono">{"{code}"}</span> and <span className="font-mono">{"{title}"}</span>)</span></Label>
                  <Textarea id="addon-code-message" data-testid="addon-code-message" rows={4} value={editing.code_message || ""} onChange={(e) => setEditing({ ...editing, code_message: e.target.value })} placeholder="Leave blank to use the default message" />
                </div>
              )}
              {!editing.link_url && (
                <div><Label className="mb-1.5 block">Message Image / QR Code <span className="text-brand-forest/40">(optional — appears in the guest's email; sent as a picture message for texts)</span></Label>
                  <MediaUpload testid="addon-code-image" value={editing.code_image_url} onUploaded={(d) => setEditing({ ...editing, code_image_url: d.url })} onClear={() => setEditing({ ...editing, code_image_url: "" })} />
                </div>
              )}
              <div className="rounded-xl border border-black/5 bg-brand-sand/40 p-4 space-y-4">
                <ItemListEditor testid="addon-returnable" label="Items to return" hint="(guest must return these to the locker before checkout — triggers the acknowledgment + night-before reminder)" items={editing.returnable_items} onChange={(v) => setEditing({ ...editing, returnable_items: v })} />
                <ItemListEditor testid="addon-keepable" label="Items to keep" hint="(items the guest gets to keep — shown for reference)" items={editing.keepable_items} onChange={(v) => setEditing({ ...editing, keepable_items: v })} />
              </div>

              {destinationCount >= 2 && (
                <div className="rounded-xl border-2 border-brand-terracotta/30 bg-brand-terracotta/5 p-4 space-y-3" data-testid="addon-hub-panel">
                  <Label className="mb-0.5 block flex items-center gap-2"><QrCode size={15} className="text-brand-terracotta" /> Combined QR (opens all selections) <span className="text-brand-forest/40">(one code → a hub linking to all {destinationCount} guides)</span></Label>
                  {editing.id ? (
                    <div className="flex flex-col sm:flex-row items-center gap-4 pt-1">
                      <div className="p-3 bg-white rounded-2xl border border-black/5" data-testid="hub-qr-code">
                        <QRCodeCanvas value={hubUrl} size={148} fgColor="#2C3B2E" bgColor="#ffffff" level="M" includeMargin />
                      </div>
                      <div className="flex-1 w-full">
                        <Label className="mb-1.5 block text-xs uppercase tracking-wide text-brand-forest/50">Hub link <span className="normal-case tracking-normal text-brand-forest/40">(save to update)</span></Label>
                        <div className="flex gap-2">
                          <Input readOnly value={hubUrl} data-testid="hub-url" className="bg-white text-sm" />
                          <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(hubUrl); toast.success("Link copied"); }} data-testid="hub-copy" className="rounded-full shrink-0"><Copy size={16} /></Button>
                        </div>
                        <Button type="button" onClick={() => printQRFrom("hub-qr-code", "Scan to open this pack's guides")} data-testid="hub-print" className="mt-3 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white"><Printer size={16} className="mr-2" />Print QR</Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-brand-terracotta bg-white rounded-lg px-3 py-2">Save this add-on first, then reopen it to see and print the combined QR code.</p>
                  )}
                </div>
              )}

              <div className="rounded-xl border border-black/5 bg-brand-sand/40 p-4 space-y-3" data-testid="addon-guide-panel">
                <GuidePagesEditor pages={editing.guide_pages} editingId={editing.id} onChange={(v) => setEditing({ ...editing, guide_pages: v })} />
              </div>

              <div className="rounded-xl border border-black/5 bg-brand-sand/40 p-4 space-y-3" data-testid="addon-info-panel">
                <div>
                  <Label htmlFor="addon-info-content" className="mb-1.5 block flex items-center gap-2"><QrCode size={15} className="text-brand-terracotta" /> Info Page / QR List <span className="text-brand-forest/40">(optional — paste a list; guests open it by scanning the QR code below)</span></Label>
                  <Textarea id="addon-info-content" data-testid="addon-info-content" rows={6}
                    value={editing.info_content || ""}
                    placeholder={"First line = title\nSecond line = short description\nSECTION HEADER (all caps)\nItem one\nItem two"}
                    onChange={(e) => setEditing({ ...editing, info_content: e.target.value })} />
                  <p className="text-xs text-brand-forest/50 mt-1.5">Tip: put the page title on the first line, a short description on the second, and use ALL-CAPS lines as section headers.</p>
                </div>
                {editing.info_content?.trim() && (
                  editing.id ? (
                    <div className="flex flex-col sm:flex-row items-center gap-4 pt-1">
                      <div className="p-3 bg-white rounded-2xl border border-black/5" data-testid="pack-qr-code">
                        <QRCodeCanvas value={packUrl} size={148} fgColor="#2C3B2E" bgColor="#ffffff" level="M" includeMargin />
                      </div>
                      <div className="flex-1 w-full">
                        <Label className="mb-1.5 block text-xs uppercase tracking-wide text-brand-forest/50">Public list link</Label>
                        <div className="flex gap-2">
                          <Input readOnly value={packUrl} data-testid="pack-url" className="bg-white text-sm" />
                          <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(packUrl); toast.success("Link copied"); }} data-testid="pack-copy" className="rounded-full shrink-0"><Copy size={16} /></Button>
                        </div>
                        <Button type="button" onClick={() => printQRFrom("pack-qr-code", "Scan to view the list")} data-testid="pack-print" className="mt-3 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white"><Printer size={16} className="mr-2" />Print QR</Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-brand-terracotta bg-brand-terracotta/10 rounded-lg px-3 py-2">Save this add-on first, then reopen it to see and print the QR code.</p>
                  )
                )}
              </div>

              <div className="rounded-xl border border-black/5 bg-brand-sand/40 p-4 space-y-3" data-testid="addon-map-panel">
                <MapSpotsEditor spots={editing.map_spots} onChange={(v) => setEditing({ ...editing, map_spots: v })} />
                {editing.map_spots?.length > 0 && (
                  editing.id ? (
                    <div className="flex flex-col sm:flex-row items-center gap-4 pt-3 border-t border-black/5">
                      <div className="p-3 bg-white rounded-2xl border border-black/5" data-testid="map-qr-code">
                        <QRCodeCanvas value={mapUrl} size={148} fgColor="#2C3B2E" bgColor="#ffffff" level="M" includeMargin />
                      </div>
                      <div className="flex-1 w-full">
                        <Label className="mb-1.5 block text-xs uppercase tracking-wide text-brand-forest/50">Picnic map link <span className="normal-case tracking-normal text-brand-forest/40">(save to update)</span></Label>
                        <div className="flex gap-2">
                          <Input readOnly value={mapUrl} data-testid="map-url" className="bg-white text-sm" />
                          <Button type="button" variant="outline" onClick={() => { navigator.clipboard.writeText(mapUrl); toast.success("Link copied"); }} data-testid="map-copy" className="rounded-full shrink-0"><Copy size={16} /></Button>
                        </div>
                        <Button type="button" onClick={() => printQRFrom("map-qr-code", "Scan to view the picnic spots map")} data-testid="map-print" className="mt-3 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white"><Printer size={16} className="mr-2" />Print QR</Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-brand-terracotta bg-brand-terracotta/10 rounded-lg px-3 py-2">Save this add-on first, then reopen it to see and print the map QR code.</p>
                  )
                )}
              </div>

              <div><Label className="mb-1.5 block">Image</Label>
                <MediaUpload testid="addon-image" value={editing.image_url} onUploaded={(d) => setEditing({ ...editing, image_url: d.url })} onClear={() => setEditing({ ...editing, image_url: "" })} />
              </div>
              <div><Label className="mb-1.5 block">Business Card Image <span className="text-brand-forest/40">(if set, the guest button opens this full-size image instead of a Request button)</span></Label>
                <MediaUpload testid="addon-card-image" value={editing.card_image_url} onUploaded={(d) => setEditing({ ...editing, card_image_url: d.url })} onClear={() => setEditing({ ...editing, card_image_url: "" })} />
              </div>
              <div><Label className="mb-1.5 block">Promo Video <span className="text-brand-forest/40">(optional — loops in the idle ad screensaver)</span></Label>
                <MediaUpload testid="addon-video" type="video" value={editing.video_url} onUploaded={(d) => setEditing({ ...editing, video_url: d.url })} onClear={() => setEditing({ ...editing, video_url: "" })} />
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="addon-save" className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white"><Save size={16} className="mr-2" />Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
